import { useCallback, useEffect, useRef, useState } from 'react'
import { useElementResize } from '@/hooks/useElementResize'
import { CARD_CLASS } from './SiteHealthStyles'
import type { SnapshotFrameProps } from './siteHealthInspector.types'
import { applySelection, mountPins, revealSelection } from './snapshotOverlay'

// The replay is inert markup: no scripts, no network, and no access to the parent origin.
const SANDBOX = 'allow-same-origin'

export default function SnapshotFrame({
  html,
  pageUrl,
  pins,
  selection,
  onSelect,
  onOpenDetails,
}: SnapshotFrameProps) {
  const frameRef = useRef<HTMLIFrameElement>(null)
  const [loadedHtml, setLoadedHtml] = useState<string | null>(null)

  // The keyed iframe remounts on new markup, so loadedHtml gates the overlay on that document.
  const loadedDocument =
    loadedHtml === html ? frameRef.current?.contentDocument : null

  const drawOverlay = useCallback(() => {
    if (!loadedDocument) return

    mountPins(loadedDocument, pins, {
      select: onSelect,
      openDetails: onOpenDetails,
    })
    applySelection(loadedDocument, selection)
  }, [loadedDocument, pins, selection, onSelect, onOpenDetails])

  useEffect(() => {
    if (!loadedDocument) return

    drawOverlay()

    // A web font swap moves the audited elements without resizing the document.
    const fonts = loadedDocument.fonts

    if (!fonts) return

    let stale = false

    void fonts.ready.then(() => {
      if (!stale) drawOverlay()
    })

    return () => {
      stale = true
    }
  }, [loadedDocument, drawOverlay])

  // Keyed on the selected pin only. A redraw must not fight the reader's own scroll.
  useEffect(() => {
    if (!loadedDocument) return

    revealSelection(loadedDocument, selection)
  }, [loadedDocument, selection])

  useElementResize(loadedDocument?.documentElement, drawOverlay)

  return (
    <div className={`${CARD_CLASS} overflow-hidden`}>
      <iframe
        key={html}
        ref={frameRef}
        title={`Audited snapshot of ${pageUrl}`}
        srcDoc={html}
        sandbox={SANDBOX}
        onLoad={() => setLoadedHtml(html)}
        className="h-[70vh] w-full border-0 bg-white"
      />
    </div>
  )
}
